import { supabase } from '../clients/supabaseClient';
import type { Delivery, Driver, OrderWithActiveDelivery } from '../types';
import { ORDER_STATUS } from '../constants/orderStatuses';
import { unwrapSupabaseJoin } from '../utils/supabase';

// ── Types ──────────────────────────────────────────────────────────────────────

export type DeliveryOption = {
    id: string;
    type: string;
    amount: number;
    valid_from: string;
    valid_to: string | null;
};

export type FreeDeliveryConfig = {
    id: string;
    threshold_cents: number;
    is_active: boolean;
};

export type NewDeliveryPayload = {
    order_id: string;
    customer_id: string | number;
    destination_id: string;
    driver_id: string;
    status: string;
    status_id?: string;
};

export type ActiveDriverDeliveryDestination = {
    driver_id: string;
    status: string;
    addresses: {
        latitude: number | null;
        longitude: number | null;
    } | null;
};

// ── Delivery options ───────────────────────────────────────────────────────────

// Fetches all delivery options that are currently active (valid_to is null or in the future).
// Used by the delivery fees config page to show which pricing tiers are live.
export async function fetchActiveDeliveryOptionsFromDb(): Promise<DeliveryOption[]> {
    const now = new Date().toISOString();
    const { data, error } = await supabase
        .from('delivery_options')
        .select('*')
        .or(`valid_to.is.null,valid_to.gt.${now}`)
        .order('type', { ascending: true });

    if (error) throw error;
    return (data ?? []) as DeliveryOption[];
}

// Inserts one or more new delivery option records (type, amount, validity window).
// Called when an admin adds or updates delivery fee tiers.
export async function insertDeliveryOptionsInDb(
    options: { type: string; amount: number; valid_from: string; valid_to?: string }[]
): Promise<void> {
    const { error } = await supabase.from('delivery_options').insert(options);
    if (error) throw error;
}

// Archives a delivery option by setting its valid_to to now, effectively expiring it.
// Preferred over hard deletion to preserve pricing history.
export async function archiveDeliveryOptionInDb(id: string): Promise<void> {
    const { error } = await supabase
        .from('delivery_options')
        .update({ valid_to: new Date().toISOString() })
        .eq('id', id);
    if (error) throw error;
}

// ── Free delivery config ───────────────────────────────────────────────────────

// Fetches the currently active free delivery threshold config, if one exists.
// Returns null if no active config is found (PGRST116 = no rows is treated as null, not an error).
export async function fetchFreeDeliveryConfigFromDb(): Promise<FreeDeliveryConfig | null> {
    const { data, error } = await supabase
        .from('free_delivery_config')
        .select('*')
        .eq('is_active', true)
        .single();

    if (error && error.code !== 'PGRST116') throw error;
    return (data as FreeDeliveryConfig) ?? null;
}

// Creates or updates the free delivery threshold.
// If an existing record ID is passed, updates it in place; otherwise inserts a new active record.
export async function upsertFreeDeliveryConfigInDb(
    existingId: string | null,
    threshold_cents: number
): Promise<void> {
    if (existingId) {
        const { error } = await supabase
            .from('free_delivery_config')
            .update({ threshold_cents, updated_at: new Date().toISOString() })
            .eq('id', existingId);
        if (error) throw error;
    } else {
        const { error } = await supabase
            .from('free_delivery_config')
            .insert({ threshold_cents, is_active: true });
        if (error) throw error;
    }
}

// Enables or disables the free delivery threshold without deleting the config record.
// Allows admins to quickly turn the feature on/off without losing the threshold value.
export async function toggleFreeDeliveryInDb(id: string, is_active: boolean): Promise<void> {
    const { error } = await supabase
        .from('free_delivery_config')
        .update({ is_active, updated_at: new Date().toISOString() })
        .eq('id', id);
    if (error) throw error;
}

// ── Deliveries ─────────────────────────────────────────────────────────────────

// Creates a new delivery record and stamps assigned_at and last_updated to now.
// Returns the full inserted record for downstream use (e.g. linking to status history).
export async function createDeliveryInDb(delivery: NewDeliveryPayload) {
    const { data, error } = await supabase
        .from('deliveries')
        .insert([{
            ...delivery,
            assigned_at:  new Date().toISOString(),
            last_updated: new Date().toISOString(),
        }])
        .select()
        .single();

    if (error) throw error;
    return data;
}

// Fetches all deliveries with full joins: driver, customer, order, and destination address.
// Used by the Deliveries list page. Returns newest first.
export async function fetchDeliveriesFromDb(): Promise<Delivery[]> {
    const { data, error } = await supabase
        .from('deliveries')
        .select(`
            *,
            driver:drivers(*),
            customer:customers(*),
            order:orders(*),
            destination:addresses(id, latitude, longitude, address_1)
        `)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as Delivery[];
}

// Given a list of drivers, returns their current active delivery destinations.
// Used by the live map to plot driver locations. Only returns DRIVER_ASSIGNED deliveries.
// Unwraps the Supabase join on addresses to normalise latitude/longitude as numbers.
export async function fetchActiveDriverDeliveryDestinationsFromDb(
    drivers: Pick<Driver, 'auth_id'>[]
): Promise<ActiveDriverDeliveryDestination[]> {
    const driverIds = drivers.map(d => d.auth_id).filter(Boolean);
    if (driverIds.length === 0) return [];

    const { data, error } = await supabase
        .from('deliveries')
        .select(`
            driver_id,
            status,
            addresses:destination_id ( latitude, longitude )
        `)
        .in('driver_id', driverIds)
        .eq('status', ORDER_STATUS.DRIVER_ASSIGNED);

    if (error) throw error;

    return (data ?? []).map(row => {
        const address = unwrapSupabaseJoin(row.addresses as { latitude: number | null; longitude: number | null } | { latitude: number | null; longitude: number | null }[] | null);
        return {
            driver_id: String(row.driver_id),
            status:    String(row.status),
            addresses: address
                ? {
                    latitude:  address.latitude  === null ? null : Number(address.latitude),
                    longitude: address.longitude === null ? null : Number(address.longitude),
                }
                : null,
        };
    });
}

// Returns the total count of all delivery records, used for analytics/stats displays.
export async function fetchTotalDeliveryCountFromDb(): Promise<number> {
    const { count, error } = await supabase
        .from('deliveries')
        .select('*', { count: 'exact', head: true });

    if (error) throw error;
    return count ?? 0;
}

// Fetches orders that currently have an active delivery, via the orders_with_driver_details DB view.
// Used by the Active Deliveries page to show in-progress driver assignments.
export async function fetchOrdersWithActiveDeliveriesFromDb(): Promise<OrderWithActiveDelivery[]> {
    const { data, error } = await supabase
        .from('orders_with_driver_details')
        .select('*')
        .order('assigned_date', { ascending: false });

    if (error) throw error;
    return (data ?? []) as OrderWithActiveDelivery[];
}

// ── Driver assignment ──────────────────────────────────────────────────────────

async function fetchStatusIdLocal(name: string): Promise<string | undefined> {
    const { data } = await supabase
        .from('statuses')
        .select('id')
        .eq('name', name)
        .single();
    return data?.id;
}

// Assigns a driver to an order. Guards against duplicate assignment:
// - Throws if an active delivery (DRIVER_ASSIGNED or ON_ROUTE) already exists.
// - Reuses a stale unassigned delivery row rather than creating a second one.
//
// The triggers sync_delivery_status_to_orders and proc_log_status_history handle
// updating orders.status and writing order_status_history automatically when
// the deliveries row is updated — so we do NOT touch those here.
export async function assignDriverToOrderInDb(
    orderId: string,
    customerId: string | number,
    destinationId: string,
    driverId: string,
): Promise<boolean> {
    const driverAssignedStatusId = await fetchStatusIdLocal(ORDER_STATUS.DRIVER_ASSIGNED);
    if (!driverAssignedStatusId) return false;

    // Guard: check for an already-active delivery on this order.
    const { data: existingDeliveries } = await supabase
        .from('deliveries')
        .select('id, driver_id, status')
        .eq('order_id', orderId)
        .in('status', [ORDER_STATUS.DRIVER_ASSIGNED, ORDER_STATUS.ON_ROUTE]);

    if (existingDeliveries && existingDeliveries.length > 0) {
        const active = existingDeliveries[0];
        if (active.status === ORDER_STATUS.ON_ROUTE) {
            throw new Error('This order already has a driver on route and cannot be reassigned.');
        }
        if (active.driver_id === driverId) {
            throw new Error('This driver is already assigned to this order.');
        }
        throw new Error('This order already has a driver pending acceptance. Unassign them first.');
    }

    // Reuse an existing unassigned delivery row if one exists, otherwise create new.
    // Updating/inserting the deliveries row triggers sync_delivery_status_to_orders
    // which updates orders.status and writes order_status_history with driver_id stamped.
    const { data: staleDelivery } = await supabase
        .from('deliveries')
        .select('id')
        .eq('order_id', orderId)
        .is('driver_id', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    if (staleDelivery) {
        const { error } = await supabase
            .from('deliveries')
            .update({
                driver_id:      driverId,
                destination_id: destinationId,
                status:         ORDER_STATUS.DRIVER_ASSIGNED,
                status_id:      driverAssignedStatusId,
                assigned_at:    new Date().toISOString(),
                last_updated:   new Date().toISOString(),
            })
            .eq('id', staleDelivery.id);
        if (error) throw error;
    } else {
        await createDeliveryInDb({
            order_id:       orderId,
            customer_id:    customerId,
            destination_id: destinationId,
            driver_id:      driverId,
            status:         ORDER_STATUS.DRIVER_ASSIGNED,
            status_id:      driverAssignedStatusId,
        });
    }

    return true;
}

// Removes a driver from a delivery by nulling driver_id and reverting status to ASSIGN_DRIVER.
//
// The trigger sync_delivery_status_to_orders fires on the deliveries UPDATE and
// automatically reverts orders.status and writes to order_status_history.
// However it stamps driver_id from the delivery row — which we're about to null —
// so we capture the revoked driver first and backfill model_id after the trigger runs.
export async function unassignDriverFromOrderInDb(orderId: string | number): Promise<void> {
    const assignDriverStatusId = await fetchStatusIdLocal(ORDER_STATUS.ASSIGN_DRIVER);

    // Capture the driver being revoked before we null them.
    const { data: activeDelivery } = await supabase
        .from('deliveries')
        .select('id, driver_id')
        .eq('order_id', String(orderId))
        .eq('status', ORDER_STATUS.DRIVER_ASSIGNED)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    const revokedDriverId = activeDelivery?.driver_id ?? null;

    // Update the delivery — this triggers sync_delivery_status_to_orders which
    // reverts orders.status to ASSIGN_DRIVER and writes an order_status_history row.
    const { error } = await supabase
        .from('deliveries')
        .update({
            driver_id:    null,
            status:       ORDER_STATUS.ASSIGN_DRIVER,
            status_id:    assignDriverStatusId,
            last_updated: new Date().toISOString(),
        })
        .eq('order_id', String(orderId))
        .eq('status', ORDER_STATUS.DRIVER_ASSIGNED);

    if (error) throw error;

    // Backfill the revoked driver onto the history row the trigger just wrote,
    // so the timeline can show "ASSIGN DRIVER — Driver: [name] (REVOKED)".
    if (revokedDriverId) {
        const { data: latestLog } = await supabase
            .from('order_status_history')
            .select('id')
            .eq('order_id', String(orderId))
            .eq('status_id', assignDriverStatusId)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

        if (latestLog) {
            await supabase
                .from('order_status_history')
                .update({ model_id: revokedDriverId, model_type: 'Driver' })
                .eq('id', latestLog.id);
        }
    }
}

// Fetches all delivery records for a given order, with the driver joined in.
// Returns newest first. An order can have multiple deliveries if drivers were reassigned.
export async function fetchDeliveriesForOrderFromDb(orderId: string) {
    const { data, error } = await supabase
        .from('deliveries')
        .select('*, driver:drivers(*)')
        .eq('order_id', orderId)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return data ?? [];
}

// Fetches all GPS location pings recorded for a specific delivery.
// Used by the Delivery Tracking tab to render the driver's route on the map.
export async function fetchDriverLocationsForDeliveryFromDb(deliveryId: string) {
    const { data, error } = await supabase
        .from('driver_locations')
        .select('*')
        .eq('delivery_id', deliveryId);

    if (error) throw error;
    return data ?? [];
}

// Fetches all deliveries that are not yet DELIVERED or CANCELLED, with full joins.
// Used by the active deliveries / dispatch views to show what's currently in progress.
export async function fetchActiveDeliveriesFromDb(): Promise<Delivery[]> {
    const { data, error } = await supabase
        .from('deliveries')
        .select(`
            *,
            driver:drivers(*),
            customer:customers(*),
            order:orders(*),
            destination:addresses(*)
        `)
        .not('status', 'in', '("DELIVERED", "CANCELLED")')
        .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as unknown as Delivery[];
}